import React, { useEffect, useRef } from 'react';
import './Portfolio.css';
import { FaExternalLinkAlt, FaGithub, FaSearch, FaChevronRight } from 'react-icons/fa';
import Aos from "aos";
import "aos/dist/aos.css";
import { 
  initHeroAnimations,
  initCTAAnimation,
  createFloatingParticles,
  createSVGElements
} from './PortfolioAnimations';

// Import portfolio images - these would need to be added to the images folder
import webImg1 from "../images/web1.jpg";
import webImg2 from "../images/web2.jpg";
import webImg3 from "../images/web3.jpg";
import appImg1 from "../images/app1.jpg";
import designImg1 from "../images/design1.jpg";
import gameImg1 from "../images/game1.jpg";
// Use existing images for eShoppit and Westilia until dedicated images are added
import eShoppitImg from "../images/app99.png"; // Mobile app image for eShoppit
import westiliaImg from "../images/web4.jpg"; // Web image for Westilia

export default function Portfolio() {
  // Create refs for animation containers
  const svgContainerRef = useRef(null);
  const particlesContainerRef = useRef(null);
  const ctaSvgContainerRef = useRef(null);

  useEffect(() => {
    // Initialize AOS animations
    Aos.init({
      duration: 1000,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1.0)',
      once: false,
    });

    // Initialize custom animations after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initHeroAnimations();
      initCTAAnimation();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Add SVGs to DOM after component mounts
  useEffect(() => {
    // Create and append SVGs to containers
    if (svgContainerRef.current) {
      const svgs = createSVGElements();
      svgContainerRef.current.innerHTML = svgs.join('');
    }

    // Create particles container
    if (particlesContainerRef.current) {
      const particles = createFloatingParticles();
      particlesContainerRef.current.innerHTML = particles.join('');
    }

    // Add CTA SVGs
    if (ctaSvgContainerRef.current) {
      ctaSvgContainerRef.current.innerHTML = `
        <svg class="cta-svg cta-svg-1" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="rgba(255, 255, 255, 0.05)" d="M46.5,-49.4C59.3,-35.9,68.5,-18,69.1,0.5C69.6,19.1,61.6,38.2,48.8,51.9C36,65.7,18,74.1,-1.2,75.3C-20.4,76.5,-40.9,70.5,-54.5,57.3C-68.1,44,-74.9,23.5,-73.5,4.2C-72,-15.1,-62.3,-33.1,-48.7,-46.5C-35.1,-60,-17.6,-68.8,0.4,-69.2C18.3,-69.6,36.6,-61.5,46.5,-49.4Z" transform="translate(100 100)">
            <animateTransform 
              attributeName="transform" 
              type="rotate"
              from="0 100 100"
              to="360 100 100" 
              dur="30s" 
              repeatCount="indefinite" />
          </path>
        </svg>
        
        <svg class="cta-svg cta-svg-2" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="rgba(255, 255, 255, 0.05)" d="M53.7,-59.3C68.7,-51.1,79.8,-34.1,82.8,-16.1C85.7,1.9,80.5,20.9,70.5,36.1C60.4,51.3,45.5,62.8,28.9,68.5C12.3,74.3,-6.1,74.3,-23.8,69.1C-41.5,63.9,-58.5,53.4,-67.7,38.2C-76.8,23,-78.2,3,-73.2,-15C-68.3,-33,-57.1,-49.1,-42.8,-57.2C-28.4,-65.3,-10.9,-65.6,5.1,-71.5C21.1,-77.5,42.2,-89.1,53.7,-59.3Z" transform="translate(100 100)">
            <animateTransform 
              attributeName="transform" 
              type="rotate"
              from="360 100 100"
              to="0 100 100" 
              dur="25s" 
              repeatCount="indefinite" />
          </path>
        </svg>
      `;
    }
  }, []);

  // Portfolio items data
  const portfolioItems = [
    {
      id: 1,
      title: "eShoppit Mobile App",
      category: "Mobile App",
      image: eShoppitImg,
      description: "Developed a comprehensive e-commerce mobile application that enables users to buy and sell products easily. Features include user authentication, product listings, search functionality, and secure payment processing.",
      techStack: ["React Native", "Firebase", "Redux", "Node.js"],
      github: "https://github.com",
      live: "https://example.com/eshoppit",
      isNew: true
    },
    {
      id: 2,
      title: "Westilia Website",
      category: "Web Development",
      image: westiliaImg,
      description: "Designed and developed a modern, responsive website for Westilia with intuitive navigation and engaging user experience. The site features custom animations and responsive design.",
      techStack: ["React", "SASS", "JavaScript", "Netlify"],
      github: "https://github.com",
      live: "https://westilia.netlify.app/",
      isNew: true
    },
    {
      id: 3,
      title: "Corporate Website Redesign",
      category: "Web Development",
      image: webImg2,
      description: "Redesigned and developed a modern, responsive website for a corporate client, featuring custom animations and a content management system.",
      techStack: ["React", "Node.js", "MongoDB", "SASS"],
      github: "https://github.com",
      live: "https://example.com"
    },
    {
      id: 4,
      title: "E-commerce Platform",
      category: "Web Development",
      image: webImg3,
      description: "Built a fully functional e-commerce platform with payment integration, inventory management, and customer dashboard.",
      techStack: ["React", "Express", "PostgreSQL", "Stripe"],
      github: "https://github.com",
      live: "https://example.com"
    },
    {
      id: 5,
      title: "Health & Fitness App",
      category: "Mobile App",
      image: appImg1,
      description: "Developed a cross-platform mobile application for tracking fitness activities, nutrition, and providing personalized workout plans.",
      techStack: ["React Native", "Firebase", "Redux", "Node.js"],
      github: "https://github.com",
      live: "https://example.com"
    },
    {
      id: 6,
      title: "Restaurant Branding",
      category: "Graphic Design",
      image: designImg1,
      description: "Created brand identity including logo design, color palette, typography, and marketing materials for a high-end restaurant.",
      techStack: ["Illustrator", "Photoshop", "InDesign"],
      github: null,
      live: "https://example.com"
    },
    {
      id: 7,
      title: "Educational Game",
      category: "Game Development",
      image: gameImg1,
      description: "Designed and developed an educational game targeting primary school children to help them learn mathematics in a fun, interactive way.",
      techStack: ["Unity", "C#", "Blender", "Adobe XD"],
      github: "https://github.com",
      live: "https://example.com"
    },
    {
      id: 8,
      title: "NGO Website",
      category: "Web Development",
      image: webImg3,
      description: "Created a website for a non-profit organization with donation integration, event management, and volunteer registration system.",
      techStack: ["WordPress", "PHP", "MySQL", "JavaScript"],
      github: null,
      live: "https://example.com"
    }
  ];

  // Filter states
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [filteredItems, setFilteredItems] = React.useState(portfolioItems);
  const [isAnimating, setIsAnimating] = React.useState(false);

  // Handle filter change
  const handleFilterClick = (filter) => {
    if (isAnimating || filter === activeFilter) return;
    
    setIsAnimating(true);
    setActiveFilter(filter);
    
    // Animate items out
    const itemElements = document.querySelectorAll('.portfolio-item');
    
    itemElements.forEach((item) => {
      item.style.opacity = 0;
      item.style.transform = 'translateY(20px)';
    });
    
    // Wait for animation then filter and animate back in
    setTimeout(() => {
      if (filter === 'all') {
        setFilteredItems(portfolioItems);
      } else {
        const filtered = portfolioItems.filter(item => 
          item.category.toLowerCase().includes(filter.toLowerCase())
        );
        setFilteredItems(filtered);
      }
      
      setIsAnimating(false);
      
      // This will trigger useEffect to animate new items in
      setTimeout(() => {
        const newItemElements = document.querySelectorAll('.portfolio-item');
        newItemElements.forEach((item, index) => {
          setTimeout(() => {
            item.style.opacity = 1;
            item.style.transform = 'translateY(0)';
          }, index * 100);
        });
      }, 50);
    }, 300);
  };

  return (
    <div className="portfolio-container">
      {/* Modern Portfolio Hero Section */}
      <section className="portfolio-hero">
        <div className="portfolio-hero-content container">
          <div className="portfolio-badge" data-aos="fade-up">Our Work</div>
          <h1 className="portfolio-hero-title" data-aos="fade-up" data-aos-delay="100">
            Featured <span className="text-highlight">Projects</span>
          </h1>
          <p className="portfolio-hero-subtitle" data-aos="fade-up" data-aos-delay="200">
            Explore our collection of successful projects that demonstrate our expertise and commitment to quality.
          </p>
        </div>
        
        {/* Interactive SVG Container */}
        <div className="interactive-svg-container" ref={svgContainerRef}></div>
        
        {/* Particles Container */}
        <div className="particles-wrapper" ref={particlesContainerRef}></div>
        
        <div className="portfolio-wave"></div>
      </section>

      {/* Portfolio Featured Section for eShoppit */}
      <section className="portfolio-featured-section">
        <div className="container">
          <div className="featured-project-wrapper">
            <div className="featured-project-content" data-aos="fade-right">
              <div className="featured-badge">Featured Project</div>
              <h2>eShoppit Mobile App</h2>
              <p>Our latest e-commerce mobile application for a seamless shopping experience. Buy and sell products with ease through this intuitive and feature-rich platform.</p>
              <div className="featured-tech-stack">
                <span className="tech-item">React Native</span>
                <span className="tech-item">Firebase</span>
                <span className="tech-item">Redux</span>
                <span className="tech-item">Node.js</span>
              </div>
              <a href="/portfolio" className="featured-link">
                View Project <FaChevronRight />
              </a>
            </div>
            <div className="featured-project-illustration" data-aos="fade-left">
              {/* Mobile App SVG will be injected here */}
              <svg className="interactive-svg mobile-svg" viewBox="0 0 300 600" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="phoneGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(44, 60, 84, 0.2)" />
                    <stop offset="100%" stopColor="rgba(148, 44, 36, 0.2)" />
                  </linearGradient>
                  <clipPath id="screenMask">
                    <rect x="40" y="80" width="220" height="440" rx="5" />
                  </clipPath>
                </defs>
                
                {/* Phone Body */}
                <rect x="30" y="40" width="240" height="520" rx="20" fill="none" stroke="rgba(44, 60, 84, 0.2)" strokeWidth="2" />
                
                {/* Phone Screen */}
                <rect x="40" y="80" width="220" height="440" rx="5" fill="url(#phoneGradient)" />
                
                {/* Home Button */}
                <circle cx="150" cy="540" r="15" fill="none" stroke="rgba(44, 60, 84, 0.2)" strokeWidth="2" />
                
                {/* Camera */}
                <circle cx="150" cy="60" r="5" fill="none" stroke="rgba(44, 60, 84, 0.2)" strokeWidth="2" />
                
                {/* App Screen Content (clipped to screen) */}
                <g clipPath="url(#screenMask)">
                  {/* App Header */}
                  <rect x="40" y="80" width="220" height="60" fill="rgba(44, 60, 84, 0.2)" />
                  <circle cx="70" cy="110" r="15" fill="rgba(255, 255, 255, 0.3)" />
                  <rect x="100" y="100" width="120" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                  <rect x="100" y="120" width="80" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                  
                  {/* App Content - Shopping Items */}
                  <g className="shopping-item" transform="translate(50, 160)">
                    <rect width="90" height="90" rx="5" fill="rgba(255, 255, 255, 0.2)" />
                    <rect y="100" width="90" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                    <rect y="120" width="60" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                  </g>
                  
                  <g className="shopping-item" transform="translate(160, 160)">
                    <rect width="90" height="90" rx="5" fill="rgba(255, 255, 255, 0.2)" />
                    <rect y="100" width="90" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                    <rect y="120" width="60" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                  </g>
                  
                  <g className="shopping-item" transform="translate(50, 300)">
                    <rect width="90" height="90" rx="5" fill="rgba(255, 255, 255, 0.2)" />
                    <rect y="100" width="90" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                    <rect y="120" width="60" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                  </g>
                  
                  <g className="shopping-item" transform="translate(160, 300)">
                    <rect width="90" height="90" rx="5" fill="rgba(255, 255, 255, 0.2)" />
                    <rect y="100" width="90" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                    <rect y="120" width="60" height="10" rx="5" fill="rgba(255, 255, 255, 0.3)" />
                  </g>
                  
                  {/* Bottom Navigation */}
                  <rect x="40" y="460" width="220" height="60" fill="rgba(44, 60, 84, 0.2)" />
                  <circle cx="80" cy="490" r="10" fill="rgba(255, 255, 255, 0.3)" />
                  <circle cx="150" cy="490" r="10" fill="rgba(255, 255, 255, 0.3)" />
                  <circle cx="220" cy="490" r="10" fill="rgba(255, 255, 255, 0.3)" />
                </g>
                
                {/* Animated elements */}
                <circle className="notification-dot" cx="220" cy="100" r="8" fill="rgba(148, 44, 36, 0.6)">
                  <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
                </circle>
                
                {/* Shopping cart */}
                <g className="shopping-cart" transform="translate(240, 400)">
                  <circle cx="10" cy="20" r="5" fill="rgba(44, 60, 84, 0.4)" />
                  <circle cx="25" cy="20" r="5" fill="rgba(44, 60, 84, 0.4)" />
                  <path d="M5,5 L30,5 L25,15 L8,15 Z" fill="rgba(44, 60, 84, 0.4)" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Filter Section */}
      <section className="portfolio-filter-section">
        <div className="container">
          <div className="portfolio-filters" data-aos="fade-up">
            <button 
              className={activeFilter === 'all' ? 'filter-btn active' : 'filter-btn'} 
              onClick={() => handleFilterClick('all')}
            >
              All
            </button>
            <button 
              className={activeFilter === 'web' ? 'filter-btn active' : 'filter-btn'} 
              onClick={() => handleFilterClick('web')}
            >
              Web Development
            </button>
            <button 
              className={activeFilter === 'mobile' ? 'filter-btn active' : 'filter-btn'} 
              onClick={() => handleFilterClick('mobile')}
            >
              Mobile Apps
            </button>
            <button 
              className={activeFilter === 'design' ? 'filter-btn active' : 'filter-btn'} 
              onClick={() => handleFilterClick('design')}
            >
              Graphic Design
            </button>
            <button 
              className={activeFilter === 'game' ? 'filter-btn active' : 'filter-btn'} 
              onClick={() => handleFilterClick('game')}
            >
              Game Development
            </button>
          </div>

          <div className="portfolio-grid">
            {filteredItems.map((item, index) => (
              <div 
                key={item.id} 
                className="portfolio-item" 
                data-aos="fade-up" 
                data-aos-delay={100 * (index % 3)}
                style={{ 
                  transition: 'opacity 0.5s ease, transform 0.5s ease',
                  opacity: 1, 
                  transform: 'translateY(0)'
                }}
              >
                <div className="portfolio-item-image">
                  <img src={item.image} alt={item.title} />
                  {item.isNew && <span className="new-badge">New</span>}
                  <div className="portfolio-item-hover">
                    <div className="portfolio-item-hover-content">
                      <button className="portfolio-btn view-btn" aria-label="View details">
                        <FaSearch />
                        <span className="btn-tooltip">Details</span>
                      </button>
                      {item.live && (
                        <a href={item.live} className="portfolio-btn live-btn" target="_blank" rel="noopener noreferrer" aria-label="Visit live site">
                          <FaExternalLinkAlt />
                          <span className="btn-tooltip">Live Site</span>
                        </a>
                      )}
                      {item.github && (
                        <a href={item.github} className="portfolio-btn github-btn" target="_blank" rel="noopener noreferrer" aria-label="View source code">
                          <FaGithub />
                          <span className="btn-tooltip">Source Code</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="portfolio-item-content">
                  <span className="portfolio-category">{item.category}</span>
                  <h3>{item.title} {item.isNew && <span className="new-text">New</span>}</h3>
                  <p>{item.description}</p>
                  <div className="portfolio-tech-stack">
                    {item.techStack.map((tech, i) => (
                      <span key={i} className="tech-item">{tech}</span>
                    ))}
                  </div>
                  <a href={item.live || "/portfolio"} className="portfolio-link">
                    View Project <FaChevronRight />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modern CTA Section */}
      <section className="portfolio-cta">
        <div className="container">
          <div className="portfolio-cta-content" data-aos="fade-up">
            <h2>Have a Project in Mind?</h2>
            <p>Let's work together to bring your ideas to life</p>
            <a href="/contact" className="cta-button">
              Start a Project <FaChevronRight />
            </a>
          </div>
        </div>
        
        {/* SVG animations for CTA section */}
        <div className="cta-svg-container" ref={ctaSvgContainerRef}></div>
      </section>
    </div>
  );
}